import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Video, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function JoinInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refreshMe } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/team/invite/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Invite not found"));
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    setAccepting(true);
    try {
      await axios.post(`${API_BASE}/team/accept`, { token, name, password }, { withCredentials: true });
      await refreshMe();
      toast.success("Welcome to the workspace");
      nav("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setAccepting(false); }
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="vp-card p-8 max-w-md text-center">
        <div className="vp-label mb-2">Invite</div>
        <div className="font-display text-2xl font-bold">This invite isn't active.</div>
        <p className="text-[#4A4D57] mt-2 text-sm">{err}</p>
        <Link to="/" className="vp-btn-ghost mt-6 inline-flex">Go home</Link>
      </div>
    </div>
  );

  if (!info) return <div className="min-h-screen flex items-center justify-center text-[#4A4D57]">Loading…</div>;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 vp-hero-bg">
      <div className="w-full max-w-md vp-card p-8 sm:p-10">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-[#F6F7FA] border border-[#0B5CFF]/40 flex items-center justify-center">
            <Video className="w-5 h-5 text-[#0B5CFF]" strokeWidth={1.75} />
          </div>
          <span className="font-display font-bold text-lg">Videoepreneur</span>
        </Link>
        <div className="vp-label">Team invite</div>
        <h1 className="font-display text-3xl font-bold mt-1">You're invited.</h1>
        <p className="text-[#4A4D57] mt-2 text-sm">
          <b>{info.inviter}</b> has invited <b>{info.email}</b> to join their workspace as <span className="vp-chip vp-chip-accent">{info.role}</span>
        </p>
        <form className="mt-6 flex flex-col gap-4" onSubmit={accept}>
          <div>
            <div className="vp-label mb-2">Your name</div>
            <input required className="vp-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="join-name" />
          </div>
          <div>
            <div className="vp-label mb-2">Create password</div>
            <input required type="password" minLength={6} className="vp-input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="join-password" />
            <div className="text-xs text-[#747680] mt-1.5">Skip if you already have an account with {info.email} — we'll link automatically.</div>
          </div>
          <button disabled={accepting} type="submit" data-testid="join-accept-btn" className="vp-btn-primary inline-flex items-center justify-center gap-2">
            <UserPlus className="w-4 h-4" /> {accepting ? "Joining…" : "Accept & join workspace"}
          </button>
        </form>
      </div>
    </div>
  );
}
